import { SpinnerGap, MagnifyingGlass } from '@phosphor-icons/react';

const INPUT_CLS = "w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none";

function Field({ label, children, color = '#6B7280' }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] font-medium mb-1 block" style={{ color }}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Advanced Leads search form — six logical rows of criteria + submit.
 * The state/handlers are lifted to the parent page so this component
 * stays purely presentational.
 */
export default function LeadSearchForm({ form, sf, leadTypes, leadOrigins, leadEvents, leadResults, loading, onSearch }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4 space-y-5">
      {/* Row 1: Nº Lead + Consultor + Tipo + Origem + Descrição */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Field label="Nº Lead">
          <input data-testid="s-leadId" type="number" value={form.leadId} onChange={e => sf('leadId', e.target.value)} className={INPUT_CLS} placeholder="Nº..." />
        </Field>
        <Field label="Id Consultor">
          <input data-testid="s-consultantId" value={form.consultantId} onChange={e => sf('consultantId', e.target.value)} className={INPUT_CLS} placeholder="ID..." />
        </Field>
        <Field label="Nome Consultor">
          <input data-testid="s-consultantName" value={form.consultantName} onChange={e => sf('consultantName', e.target.value)} className={INPUT_CLS} placeholder="Nome..." />
        </Field>
        <Field label="Tipo Lead">
          <select data-testid="s-leadType" value={form.leadType} onChange={e => { sf('leadType', e.target.value); sf('companyId',''); sf('companyName',''); sf('companyEmail',''); sf('companyPhoneNumber',''); sf('companyCity',''); }} className={INPUT_CLS}>
            <option value="">Não Especificado...</option>
            {leadTypes.map(t => <option key={t.Id} value={t.Id}>{t.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Origem">
          <select data-testid="s-leadOrigin" value={form.leadOrigin} onChange={e => sf('leadOrigin', e.target.value)} className={INPUT_CLS}>
            <option value="">Não Especificado...</option>
            {leadOrigins.map(o => <option key={o.Id} value={o.Id}>{o.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Descrição">
          <input data-testid="s-description" value={form.description} onChange={e => sf('description', e.target.value)} className={INPUT_CLS} placeholder="Descrição..." />
        </Field>
      </div>

      {/* Row 2: Valor, Datas, Referência, Observação, Encomenda */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Field label="Valor Previsto">
          <input data-testid="s-value" type="number" value={form.value} onChange={e => sf('value', e.target.value)} className={INPUT_CLS} placeholder="Valor..." />
        </Field>
        <Field label="Data Início">
          <input data-testid="s-initDate" type="date" value={form.initDate} onChange={e => sf('initDate', e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Data Prevista de Fim">
          <input data-testid="s-endDate" type="date" value={form.endDate} onChange={e => sf('endDate', e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Referência">
          <input data-testid="s-ref" value={form.ref} onChange={e => sf('ref', e.target.value)} className={INPUT_CLS} placeholder="Ref..." />
        </Field>
        <Field label="Observação">
          <input data-testid="s-observations" value={form.observations} onChange={e => sf('observations', e.target.value)} className={INPUT_CLS} placeholder="Obs..." />
        </Field>
        <Field label="ID Encomenda">
          <input data-testid="s-orderId" type="number" value={form.orderId} onChange={e => sf('orderId', e.target.value)} className={INPUT_CLS} placeholder="ID..." />
        </Field>
      </div>

      {/* Row 3: Cliente */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Field label="Id Cliente" color="#2563EB">
          <input data-testid="s-clientId" type="number" value={form.clientId} onChange={e => sf('clientId', e.target.value)} className={INPUT_CLS} placeholder="ID..." />
        </Field>
        <Field label="Nome Cliente" color="#2563EB">
          <input data-testid="s-clientName" value={form.clientName} onChange={e => sf('clientName', e.target.value)} className={INPUT_CLS} placeholder="Nome..." />
        </Field>
        <Field label="Email Cliente" color="#2563EB">
          <input data-testid="s-clientEmail" value={form.clientEmail} onChange={e => sf('clientEmail', e.target.value)} className={INPUT_CLS} placeholder="Email..." />
        </Field>
        <Field label="Telemóvel Cliente" color="#2563EB">
          <input data-testid="s-clientPhoneNumber" value={form.clientPhoneNumber} onChange={e => sf('clientPhoneNumber', e.target.value)} className={INPUT_CLS} placeholder="Tel..." />
        </Field>
        <Field label="Localidade Cliente" color="#2563EB">
          <input data-testid="s-clientCity" value={form.clientCity} onChange={e => sf('clientCity', e.target.value)} className={INPUT_CLS} placeholder="Localidade..." />
        </Field>
      </div>

      {/* Row 4: Empresa (only if BB) */}
      {form.leadType === 'BB' && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Id Empresa" color="#7C3AED">
            <input data-testid="s-companyId" value={form.companyId} onChange={e => sf('companyId', e.target.value)} className={INPUT_CLS} placeholder="ID..." />
          </Field>
          <Field label="Nome Empresa" color="#7C3AED">
            <input data-testid="s-companyName" value={form.companyName} onChange={e => sf('companyName', e.target.value)} className={INPUT_CLS} placeholder="Nome..." />
          </Field>
          <Field label="Email Empresa" color="#7C3AED">
            <input data-testid="s-companyEmail" value={form.companyEmail} onChange={e => sf('companyEmail', e.target.value)} className={INPUT_CLS} placeholder="Email..." />
          </Field>
          <Field label="Telemóvel Empresa" color="#7C3AED">
            <input data-testid="s-companyPhoneNumber" value={form.companyPhoneNumber} onChange={e => sf('companyPhoneNumber', e.target.value)} className={INPUT_CLS} placeholder="Tel..." />
          </Field>
          <Field label="Localidade Empresa" color="#7C3AED">
            <input data-testid="s-companyCity" value={form.companyCity} onChange={e => sf('companyCity', e.target.value)} className={INPUT_CLS} placeholder="Localidade..." />
          </Field>
        </div>
      )}

      {/* Row 5: Evolução */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Field label="Fase Atual Evolução" color="#059669">
          <select data-testid="s-contactState" value={form.contactState} onChange={e => sf('contactState', e.target.value)} className={INPUT_CLS}>
            <option value="">Não Especificado...</option>
            {leadEvents.map(e => <option key={e.ID_EstadoNegocio} value={e.ID_EstadoNegocio}>{e.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Data Início Evolução" color="#059669">
          <input data-testid="s-contactInitDate" type="date" value={form.contactInitDate} onChange={e => sf('contactInitDate', e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Data Fim Evolução" color="#059669">
          <input data-testid="s-contactEndDate" type="date" value={form.contactEndDate} onChange={e => sf('contactEndDate', e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Resultado Evolução" color="#059669">
          <select data-testid="s-contactResult" value={form.contactResult} onChange={e => sf('contactResult', e.target.value)} className={INPUT_CLS}>
            <option value="">Não Especificado...</option>
            {leadResults.map(r => <option key={r.Id} value={r.Id}>{r.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Observação Evolução" color="#059669">
          <input data-testid="s-contactObservation" value={form.contactObservation} onChange={e => sf('contactObservation', e.target.value)} className={INPUT_CLS} placeholder="Obs..." />
        </Field>
      </div>

      {/* Row 6: Profundidade + Botão */}
      <div className="flex items-end gap-4">
        <div className="w-48">
          <Field label="Nível de profundidade da rede">
            <input data-testid="s-depth" type="number" min="1" max="999" value={form.profundidadeRede} onChange={e => sf('profundidadeRede', e.target.value)} className={INPUT_CLS} />
          </Field>
        </div>
        <button data-testid="search-btn" onClick={onSearch} disabled={loading}
          className="flex items-center gap-1.5 px-6 py-2 bg-[#0F1829] text-white text-sm font-medium rounded hover:bg-[#001f7a] transition-colors disabled:opacity-50">
          {loading ? <SpinnerGap size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />} Pesquisar
        </button>
      </div>
    </div>
  );
}
